import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { CalendarDays, Clock, MapPin, Sparkles } from "lucide-react";
import { api, fileUrl, fmtDate } from "@/lib/api";
import { txt } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

const ytId = (url) => {
  const m = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{6,})/.exec(url || "");
  return m ? m[1] : null;
};

function WelcomeSlide({ site }) {
  const words = txt(site, "welcome_headline", "Welcome to Family").split(" ");
  return (
    <div className="h-full w-full grid place-items-center text-center px-10" data-testid="welcome-slide-hero">
      <div>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}
          className="text-[11px] md:text-sm font-black uppercase tracking-[0.5em] text-amber-300">GIM Church Makassar</motion.p>
        <h1 className="mt-8 text-5xl md:text-8xl font-black tracking-tighter text-white leading-[0.95] flex flex-wrap justify-center gap-x-5">
          {words.map((w, i) => (
            <motion.span key={w + i} initial={{ opacity: 0, y: 48, filter: "blur(10px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ delay: 0.25 + i * 0.12, duration: 0.9, ease: EASE }} className={i === words.length - 1 ? "text-gradient pr-2" : ""}>{w}</motion.span>
          ))}
        </h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1 }}
          className="mt-8 text-base md:text-2xl text-white/60 max-w-3xl mx-auto font-light">{txt(site, "welcome_subheadline", "")}</motion.p>
      </div>
    </div>
  );
}

function ScheduleSlide({ schedules }) {
  return (
    <div className="h-full w-full flex flex-col justify-center px-10 md:px-24" data-testid="welcome-slide-schedule">
      <p className="text-[11px] md:text-sm font-black uppercase tracking-[0.4em] text-blue-300">Jadwal Ibadah Mingguan</p>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {schedules.slice(0, 8).map((s, i) => (
          <motion.div key={s.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1, duration: 0.7, ease: EASE }}
            className="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-md p-6">
            <p className="text-4xl md:text-5xl font-black tabular-nums text-white">{s.time}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mt-1">WITA</p>
            <p className="mt-5 font-extrabold text-white">{s.title}</p>
            <p className="text-sm text-white/50 mt-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{s.day}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function EventsSlide({ events, label }) {
  return (
    <div className="h-full w-full flex flex-col justify-center px-10 md:px-24" data-testid="welcome-slide-events">
      <p className="text-[11px] md:text-sm font-black uppercase tracking-[0.4em] text-amber-300 flex items-center gap-2"><Sparkles className="w-4 h-4" />{label}</p>
      <div className="mt-10 grid md:grid-cols-3 gap-6">
        {events.slice(0, 3).map((e, i) => (
          <motion.div key={e.id} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.12, duration: 0.7, ease: EASE }}
            className="relative rounded-[2rem] overflow-hidden border border-white/10 min-h-72 flex flex-col justify-end p-7">
            {e.image_url && <img src={fileUrl(e.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
            <div className="relative">
              <p className="text-2xl md:text-3xl font-black text-white tracking-tight leading-tight">{e.title}</p>
              <p className="mt-2 text-sm text-white/60 flex items-center gap-3">
                <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(e.start_date)}</span>
                {e.time && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{e.time} WITA</span>}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function PhotoSlide({ item }) {
  return (
    <div className="absolute inset-0" data-testid="welcome-slide-photo">
      <motion.img initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 8, ease: "linear" }}
        src={fileUrl(item.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-slate-950/10" />
      {(item.caption || item.title) && (
        <div className="absolute bottom-24 left-10 md:left-24 max-w-3xl">
          <p className="text-[11px] font-black uppercase tracking-[0.4em] text-white/50">Momen Ibadah</p>
          <p className="mt-3 text-3xl md:text-5xl font-black text-white tracking-tight">{item.title || item.caption}</p>
        </div>
      )}
    </div>
  );
}

function VideoSlide({ item }) {
  const id = ytId(item.youtube_url);
  if (!id) return null;
  return (
    <div className="absolute inset-0 bg-black" data-testid="welcome-slide-video">
      <iframe title={item.title || "Video kegiatan"} className="w-full h-full"
        src={`https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${id}&modestbranding=1&rel=0&playsinline=1`}
        allow="autoplay; encrypted-media" frameBorder="0" />
      <div className="absolute bottom-24 left-10 md:left-24 pointer-events-none">
        <p className="text-[11px] font-black uppercase tracking-[0.4em] text-white/60">Kegiatan Gereja</p>
        <p className="mt-2 text-2xl md:text-4xl font-black text-white">{item.title}</p>
      </div>
    </div>
  );
}

export default function WelcomeScreen() {
  const [data, setData] = useState(null);
  const [i, setI] = useState(0);

  useEffect(() => {
    const load = () => api.get("/public/welcome").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const site = data?.site || {};
  const slides = useMemo(() => {
    if (!data) return [];
    const out = [{ key: "hero", node: <WelcomeSlide site={site} /> }];
    if (data.schedules?.length) out.push({ key: "schedule", node: <ScheduleSlide schedules={data.schedules} /> });
    if (data.worship_posts?.length) out.push({ key: "worship", node: <EventsSlide events={data.worship_posts} label="Ibadah Minggu Ini" /> });
    (data.media || []).forEach((m, idx) => {
      if (m.kind === "video" && ytId(m.youtube_url)) out.push({ key: `vid-${idx}`, node: <VideoSlide item={m} />, long: true });
      else if (m.image_url) out.push({ key: `med-${idx}`, node: <PhotoSlide item={m} /> });
    });
    (data.gallery || []).slice(0, 8).forEach((g, idx) => out.push({ key: `gal-${idx}`, node: <PhotoSlide item={g} /> }));
    const upcoming = [...(data.special_events || []), ...(data.events || [])].filter((e, idx, arr) => arr.findIndex((x) => x.id === e.id) === idx);
    if (upcoming.length) out.push({ key: "coming", node: <EventsSlide events={upcoming} label="Coming Up Event" /> });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const base = Math.max(5, Number(site.welcome_interval) || 9) * 1000;
  const current = slides[i % (slides.length || 1)];
  useEffect(() => {
    if (!slides.length) return;
    const t = setTimeout(() => setI((v) => (v + 1) % slides.length), current?.long ? base * 3 : base);
    return () => clearTimeout(t);
  }, [i, slides.length, base, current]);

  const prayUrl = `${window.location.origin}/pray`;
  return (
    <div className="fixed inset-0 bg-slate-950 overflow-hidden select-none" data-testid="welcome-screen">
      <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_10%,rgba(37,99,235,.25),transparent_55%)]" />
      <div className="absolute inset-0 grain opacity-[0.10] mix-blend-overlay pointer-events-none" />
      <AnimatePresence mode="wait">
        <motion.div key={current?.key || "empty"} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: EASE }} className="absolute inset-0">
          {current?.node || <WelcomeSlide site={site} />}
        </motion.div>
      </AnimatePresence>

      <div className="absolute bottom-8 right-8 md:bottom-10 md:right-10 flex items-center gap-4 bg-white/95 rounded-3xl p-4 md:p-5 shadow-2xl" data-testid="welcome-prayer-qr">
        <QRCodeSVG value={prayUrl} size={104} />
        <div className="max-w-[13rem]">
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-600">{txt(site, "welcome_prayer_title", "Pokok Doa Pribadi")}</p>
          <p className="mt-1.5 text-xs font-semibold text-slate-600 leading-snug">{txt(site, "welcome_prayer_text", "Pindai QR ini untuk mengirim pokok doa Anda.")}</p>
        </div>
      </div>

      <div className="absolute bottom-10 left-10 flex gap-1.5" data-testid="welcome-progress">
        {slides.map((s, idx) => (
          <span key={s.key} className={`h-1 rounded-full transition-all duration-500 ${idx === i ? "w-10 bg-amber-300" : "w-3 bg-white/25"}`} />
        ))}
      </div>
    </div>
  );
}
